import { useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import publicAxiosService from 'utils/publicAxios';
import { vehiclePageStyles } from '../page/styles';
import { Vehicle } from 'types/vehicles/types';
import { canViewField, canEditField, USER_TYPE_PUBLIC } from 'types/auth/types';
import LoadingSpinner from 'components/Shared/LoadingSpinner/LoadingSpinner';
import VehicleStatus from '../status/VehicleStatus';
import { STATUS_DELETED, STATUS_DELIVERED } from '../config';
import ErrorPage from 'routes/errorPage';
import VehicleDetailsSection, { VehicleDetailsSectionRef } from '../page/details/VehicleDetailsSection';
import VehicleGeneralSection, { VehicleGeneralSectionRef } from '../page/general/VehicleGeneralSection';
import VehicleShippingSection, { VehicleShippingSectionRef } from '../page/shipping/VehicleShippingSection';
import VehiclePicturesSection from '../page/pictures/VehiclePicturesSection';

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {};

function PublicVehiclePage() {
  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);

  const detailsSectionRef = useRef<VehicleDetailsSectionRef>(null);
  const generalSectionRef = useRef<VehicleGeneralSectionRef>(null);
  const shippingSectionRef = useRef<VehicleShippingSectionRef>(null);

  const canView = useCallback((fieldName: string) => canViewField(USER_TYPE_PUBLIC, fieldName), []);
  const canEdit = useCallback((fieldName: string) => canEditField(USER_TYPE_PUBLIC, fieldName), []);

  // Check if vehicle is deleted or delivered
  const isVehicleDeleted = vehicle?.status?.value === STATUS_DELETED;
  const isVehicleDelivered = vehicle?.status?.value === STATUS_DELIVERED;

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      publicAxiosService
        .get(`/vehicles/public/vehicle/${id}/`)
        .then(res => {
          setVehicle(res.data);
          setIsLoading(false);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [id]);

  // Update section data after vehicle is set and sections are mounted
  useEffect(() => {
    if (vehicle) {
      detailsSectionRef.current?.updateSectionData(vehicle);
      generalSectionRef.current?.updateSectionData(vehicle);
      shippingSectionRef.current?.updateSectionData(vehicle);
    }
  }, [vehicle]);

  if (isVehicleDeleted || isVehicleDelivered) {
    return <ErrorPage />;
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <LoadingSpinner />
      </Box>
    );
  }

  if (!vehicle) {
    return <ErrorPage />;
  }

  return (
    <Box sx={{ ...vehiclePageStyles.container, margin: '30px' }}>
      <Box sx={vehiclePageStyles.headerContainer}>
        <Box sx={{ display: 'flex', gap: { xs: '10px', sm: '20px' }, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Typography color="text" sx={vehiclePageStyles.headerTitle}>
            {vehicle?.name?.trim() || 'Vehicle Details'}
          </Typography>
          {vehicle?.status ? <VehicleStatus {...vehicle?.status} /> : null}
        </Box>
      </Box>

      <Box sx={vehiclePageStyles.contentBox}>
        {/* Column 1: Pictures */}
        <Box sx={vehiclePageStyles.column}>
          <VehiclePicturesSection
            vehicleId={vehicle?.id}
            initialPictures={vehicle?.pictures || []}
            initialTabs={vehicle?.tabs || []}
            canEdit={canEdit}
          />
        </Box>

        {/* Column 2: Vehicle Details + General */}
        <Box sx={vehiclePageStyles.column}>
          <VehicleDetailsSection
            ref={detailsSectionRef}
            updateSubmitData={noop}
            setIsLoading={noop}
            canView={canView}
            canEdit={canEdit}
            readOnly
          />
          <VehicleGeneralSection
            ref={generalSectionRef}
            updateSubmitData={noop}
            canView={canView}
            canEdit={canEdit}
            isSuperuser={false}
            newEntryStatus={0}
            newEntryStatusLabel=""
            user={null}
            readOnly
          />
        </Box>

        {/* Column 3: Shipping */}
        <Box sx={vehiclePageStyles.column}>
          <VehicleShippingSection
            ref={shippingSectionRef}
            updateSubmitData={noop}
            canView={canView}
            canEdit={canEdit}
            isSuperuser={false}
            readOnly
          />
        </Box>
      </Box>
    </Box>
  );
}

export default function PublicVehiclePageRoute() {
  const { id } = useParams();
  return <PublicVehiclePage key={id} />;
}
